const GOALSTYLE_CUSTOMIZATION_OPTION_NAMES = new Set([
  'customization',
  'personalización',
  'personnalisation',
]);

const GOALSTYLE_LEGACY_PLAYER_OPTION_NAMES = new Set([
  'player',
  'jugador',
  'joueur',
]);

const GOALSTYLE_CUSTOMIZATION_VALUE_ALIASES = {
  Standard: ['standard', 'estándar'],
  'Player Name': ['player name', 'nombre de jugador', 'nom du joueur'],
  'Custom Name': ['custom name', 'nombre personalizado', 'nom personnalisé'],
  Patch: ['patch'],
  'Player Name + Patch': ['player name + patch'],
  'Custom Name + Patch': ['custom name + patch'],
};

class GoalStyleJerseyOptions extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.legacyPlayerOption = this.dataset.legacyPlayerOption === 'true';
    this.directRoster = this.dataset.directRoster === 'true';
    this.playerOnly = this.dataset.playerOnly === 'true';

    this.nameSetInputs = [...this.querySelectorAll('[data-name-set]')];
    this.directPlayerInputs = [...this.querySelectorAll('[data-direct-player-choice]')];
    this.directPlayerProperty = this.querySelector('[data-direct-player-property]');
    this.playerPanel = this.querySelector('[data-panel="player"]');
    this.customPanel = this.querySelector('[data-panel="custom"]');
    this.diaPanel = this.querySelector('[data-panel="dia-de-muertos"]');
    this.diaModeInputs = [...this.querySelectorAll('[data-dia-mode]')];
    this.playerSelect = this.querySelector('[data-player-select]');
    this.customName = this.querySelector('[data-custom-name]');
    this.customNumber = this.querySelector('[data-custom-number]');
    this.patchSelect = this.querySelector('select[name="properties[Patch]"]');

    this.nameSetInputs.forEach((input) => input.addEventListener('change', () => this.update()));
    this.directPlayerInputs.forEach((input) => input.addEventListener('change', () => this.update()));
    this.diaModeInputs.forEach((input) => input.addEventListener('change', () => this.update()));
    this.playerSelect?.addEventListener('change', () => this.update());
    this.patchSelect?.addEventListener('change', () => this.update());

    this.installInternalOptionStyles();
    this.observeVariantPicker();
    this.update();
  }

  disconnectedCallback() {
    this.variantPickerObserver?.disconnect();
  }

  normalizeOptionText(value) {
    return String(value || '').trim().toLowerCase();
  }

  isInternalCustomizationInput(input) {
    return GOALSTYLE_CUSTOMIZATION_OPTION_NAMES.has(this.normalizeOptionText(input?.dataset.optionName));
  }

  isLegacyPlayerInput(input) {
    return GOALSTYLE_LEGACY_PLAYER_OPTION_NAMES.has(this.normalizeOptionText(input?.dataset.optionName));
  }

  getLegacyPlayerInputs(picker = this.getVariantPicker()) {
    if (!picker || !this.legacyPlayerOption) return [];

    return [...picker.querySelectorAll('[data-option-name]')].filter((input) =>
      this.isLegacyPlayerInput(input)
    );
  }

  getInternalCustomizationInputs(picker = this.getVariantPicker()) {
    if (!picker) return [];

    return [...picker.querySelectorAll('[data-option-name]')].filter((input) =>
      this.isInternalCustomizationInput(input)
    );
  }

  installInternalOptionStyles() {
    if (document.getElementById('goalstyle-internal-customization-style')) return;

    const style = document.createElement('style');
    style.id = 'goalstyle-internal-customization-style';
    style.textContent = `
      .variant-picker .variant-option:has([data-option-name="Customization"]),
      .variant-picker .variant-option:has([data-option-name="Personalización"]),
      .variant-picker .variant-option:has([data-option-name="Personnalisation"]) {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  getVariantPicker() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector(`variant-picker[data-product-id="${productId}"]`) ||
      document.querySelector(`variant-picker[data-product-id="${productId}"]`)
    );
  }

  observeVariantPicker() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    this.hideInternalCustomizationOption();
    this.hideLegacyPlayerOption();

    this.variantPickerObserver = new MutationObserver(() => {
      this.hideInternalCustomizationOption();
      this.hideLegacyPlayerOption();
    });
    this.variantPickerObserver.observe(picker, { childList: true, subtree: true });
  }

  hideInternalCustomizationOption() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    this.getInternalCustomizationInputs(picker).forEach((input) => {
      input.closest('.variant-option')?.setAttribute('hidden', '');
    });
  }

  hideLegacyPlayerOption() {
    if (!this.legacyPlayerOption) return;

    const picker = this.getVariantPicker();
    if (!picker) return;

    this.getLegacyPlayerInputs(picker).forEach((input) => {
      input.closest('.variant-option')?.setAttribute('hidden', '');
    });
  }

  getDirectPlayerValue() {
    return this.directPlayerInputs.find((input) => input.checked)?.value || '';
  }

  syncDirectPlayerProperty() {
    if (!this.directPlayerProperty) return;
    this.directPlayerProperty.value = this.getDirectPlayerValue();
  }

  getNameMode() {
    const checked = this.nameSetInputs.find((input) => input.checked);
    return checked?.dataset.nameSet || 'none';
  }

  getDiaMode() {
    return this.diaModeInputs.find((input) => input.checked)?.dataset.diaMode || 'player';
  }

  getCustomizationValue() {
    const mode = this.getNameMode();
    const hasPatch = Boolean(this.patchSelect && this.patchSelect.value && this.patchSelect.value !== 'No Patch');

    if (mode === 'dia-de-muertos' && hasPatch) return 'Custom Name + Patch';
    if (mode === 'dia-de-muertos') return 'Custom Name';
    if (mode === 'player' && hasPatch) return 'Player Name + Patch';
    if (mode === 'custom' && hasPatch) return 'Custom Name + Patch';
    if (mode === 'player') return 'Player Name';
    if (mode === 'custom') return 'Custom Name';
    if (hasPatch) return 'Patch';
    return 'Standard';
  }

  syncCustomizationVariant() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    this.hideInternalCustomizationOption();

    const desiredValue = this.getCustomizationValue();
    const desiredAliases = GOALSTYLE_CUSTOMIZATION_VALUE_ALIASES[desiredValue] || [this.normalizeOptionText(desiredValue)];
    const inputs = this.getInternalCustomizationInputs(picker);
    const target = inputs.find((input) => desiredAliases.includes(this.normalizeOptionText(input.value)));

    if (target && !target.checked) {
      target.click();
    }
  }

  syncLegacyPlayerVariant() {
    if (!this.legacyPlayerOption) return;

    const selectedPlayer = this.directRoster ? this.getDirectPlayerValue() : this.playerSelect?.value;
    if (!selectedPlayer) return;

    const picker = this.getVariantPicker();
    if (!picker) return;

    this.hideLegacyPlayerOption();

    const desiredPlayer = this.normalizeOptionText(selectedPlayer);
    const inputs = this.getLegacyPlayerInputs(picker);
    const target = inputs.find((input) => this.normalizeOptionText(input.value) === desiredPlayer);

    if (target && !target.checked) {
      target.click();
    }
  }

  update() {
    if (this.playerOnly) {
      if (this.playerSelect) {
        this.playerSelect.disabled = false;
        this.playerSelect.required = true;
      }
      this.syncLegacyPlayerVariant();
      return;
    }

    if (this.directRoster) {
      this.querySelectorAll('.gs-choice').forEach((choice) => {
        const input = choice.querySelector('input');
        choice.classList.toggle('is-selected', Boolean(input?.checked));
      });
      this.syncDirectPlayerProperty();
      this.syncLegacyPlayerVariant();
      return;
    }

    const mode = this.getNameMode();

    this.querySelectorAll('.gs-choice').forEach((choice) => {
      const input = choice.querySelector('input');
      choice.classList.toggle('is-selected', Boolean(input?.checked));
    });

    const diaActive = mode === 'dia-de-muertos';
    const diaMode = this.getDiaMode();
    if (this.diaPanel) this.diaPanel.hidden = !diaActive;

    const playerActive = mode === 'player' || (diaActive && diaMode === 'player');
    if (this.playerPanel) this.playerPanel.hidden = !playerActive;
    if (this.playerSelect) {
      this.playerSelect.disabled = !playerActive;
      this.playerSelect.required = playerActive;
      if (!playerActive) this.playerSelect.selectedIndex = 0;
    }

    const customActive = mode === 'custom' || (diaActive && diaMode === 'custom');
    if (this.customPanel) this.customPanel.hidden = !customActive;
    [this.customName, this.customNumber].forEach((field) => {
      if (!field) return;
      field.disabled = !customActive;
      field.required = customActive;
      if (!customActive) field.value = '';
    });

    this.syncCustomizationVariant();
    this.syncLegacyPlayerVariant();
  }
}

if (!customElements.get('goalstyle-jersey-options')) {
  customElements.define('goalstyle-jersey-options', GoalStyleJerseyOptions);
}
